import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Link,
  Alert,
  Divider as MuiDivider,
  Button as MuiButton
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../services/api';
import { TermsCheckbox } from '../../components/auth/TermsCheckbox';
import { EmailPreferencesCheckbox } from '../../components/auth/EmailPreferencesCheckbox';
import { Card } from '../../components/common/Card';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { Logo } from '../../components/layout/Logo';
import { getSelectedOrganization } from '../../utils/auth';
import { useAuth } from '../../contexts/AuthContext';

const Register: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [emailPreferences, setEmailPreferences] = useState(true);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const organization = getSelectedOrganization();
    if (!organization) {
      navigate('/organization');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const organization = getSelectedOrganization();
    if (!organization) {
      setError('Please select an organization first');
      navigate('/organization');
      return;
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms and Conditions');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      // Register the user
      await auth.register(username, password, email, emailPreferences, organization.org_id);
      
      // Automatically login after successful registration
      try {
        const loginResponse = await auth.login(username, password);
        if (loginResponse.data && loginResponse.data.token) {
          // Use the AuthContext to set the user as logged in
          const { token, user } = loginResponse.data;
          login(token, user);
          navigate('/home');
        } else {
          // If login fails after registration, redirect to login page
          navigate('/login');
        }
      } catch (loginErr) {
        console.error('Auto-login error:', loginErr);
        navigate('/login');
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Logo style={{ height: '80px', width: 'auto' }} />
          </Box>

          <Card
            sx={{
              width: '100%',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'rgba(38, 38, 38, 0.6)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                mb: 4,
                fontWeight: 700,
                color: 'text.primary'
              }}
            >
              Create Account
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                  {error}
                </Alert>
              )}

              <Input
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Input
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Input
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 2 }}
              />

              <Input
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 3 }}
              />

              <MuiDivider sx={{ my: 2, color: 'text.secondary', fontSize: '0.875rem' }}>OR</MuiDivider>

              <MuiButton
                variant="outlined"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={() => {
                  const backendBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000';
                  window.location.href = `${backendBaseUrl}/accounts/google/login/?process=login`;
                }}
                sx={{
                  mb: 2,
                  py: 1.5,
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                Sign up with Google
              </MuiButton>

              <Box sx={{ mb: 2 }}>
                <EmailPreferencesCheckbox 
                  checked={emailPreferences} 
                  onChange={setEmailPreferences} 
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TermsCheckbox 
                  checked={agreedToTerms} 
                  onChange={setAgreedToTerms} 
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!agreedToTerms}
                sx={{ 
                  mt: 2,
                  mb: 2,
                  height: '48px',
                  fontSize: '1rem'
                }}
              >
                Create Account
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Link 
                  href="/login" 
                  variant="body2"
                  sx={{ 
                    cursor: 'pointer',
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                >
                  Already have an account? Sign In
                </Link>
              </Box>
            </Box>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
